import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import { analyticsAPI, usersAPI, nasAPI, radiusServersAPI, profilesAPI, tenantsAPI } from '../api';
import { Card, Button, PageHeader, Alert } from '../components/UI';
import { useTenant } from '../TenantContext';
import { useAuth } from '../context/AuthContext';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer
} from 'recharts';

// ── helpers ───────────────────────────────────────────────────────────────────
const fmtBytes = (b) => {
  if (!b || b === 0) return '0 B';
  const n = Number(b);
  if (n >= 1e9) return (n / 1e9).toFixed(2) + ' GB';
  if (n >= 1e6) return (n / 1e6).toFixed(2) + ' MB';
  if (n >= 1e3) return (n / 1e3).toFixed(2) + ' KB';
  return n + ' B';
};

const fmtDuration = (secs) => {
  if (secs === null || secs === undefined || secs === '') return '—';
  const s = Number(secs);
  if (isNaN(s)) return '—';
  const days  = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  const mins  = Math.floor((s % 3600) / 60);
  const sec   = Math.floor(s % 60);
  const pad = (n) => String(n).padStart(2, '0');
  const hms = `${pad(hours)}:${pad(mins)}:${pad(sec)}`;
  return days > 0 ? `${days}d ${hms}` : hms;
};

const toCHDatetime = (v) => {
  if (!v) return v;
  const withSeconds = v.length === 16 ? v + ':00' : v;
  return withSeconds.replace('T', ' ');
};

const fmtBucket = (v) => {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(d)) return v;
  return d.toLocaleString('id-ID', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });
};

const fmtBucketFull = (v) => {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(d)) return v;
  return d.toLocaleString('id-ID', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
};

const PERIODS = [
  { value: '1h',    label: '1 Jam'    },
  { value: '24h',   label: '24 Jam'   },
  { value: '7d',    label: '7 Hari'   },
  { value: '30d',   label: '30 Hari'  },
  { value: 'today', label: 'Hari ini' },
  { value: 'month', label: 'Bulan ini'},
  { value: 'custom',label: 'Custom'   },
];

const GRANULARITIES = [
  { value: '5m',   label: '5 Menit'    },
  { value: 'hour', label: 'Per Jam'    },
  { value: 'day',  label: 'Per Hari'   },
  { value: 'week', label: 'Per Minggu' },
];

const GROUP_BY_OPTS = [
  { value: 'server_name',  label: 'Server Name' },
  { value: 'realm',        label: 'Realm'       },
  { value: 'username',     label: 'Username'    },
  { value: 'nasipaddress', label: 'NAS IP'      },
  { value: 'groupname',    label: 'Profile'     },
  { value: 'callingstationid', label: 'MAC Address' },
];

const REALM_FOLLOWERS = ['username', 'nasipaddress', 'groupname', 'callingstationid'];

const computeGroupBy = (current, toggled) => {
  const isActive = current.includes(toggled);
  let next = isActive
    ? current.filter(x => x !== toggled)
    : [...current, toggled];
  if (next.some(v => REALM_FOLLOWERS.includes(v)) && !next.includes('realm')) {
    next = ['realm', ...next];
  }
  return next.length ? next : ['realm'];
};

// Urutan leveling buat nentuin level PALING DALAM yang aktif — dipakai buat nentuin
// apa yang ditampilin di sumbu/label graph (Usage Summary, Top Items, Traffic, Concurrent).
// Server > Realm > NAS > Profile > Username / MAC Address.
const GROUP_HIERARCHY = ['server_name', 'realm', 'nasipaddress', 'groupname', 'username', 'callingstationid'];
const deepestGroupCol = (cols) => {
  const active = GROUP_HIERARCHY.filter(g => (cols || []).includes(g));
  return active.length ? active[active.length - 1] : 'realm';
};

const COLORS = ['var(--accent-blue)', 'var(--accent-cyan)', 'var(--accent-green)', 'var(--accent-orange)', 'var(--accent-purple)'];

// ── Searchable dropdown ───────────────────────────────────────────────────────
function SearchSelect({ label, value, onChange, options, placeholder, allowClear = true }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [pos, setPos] = useState({ top: 0, left: 0, width: 180 });
  const btnRef = useRef(null);
  const popRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const h = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (popRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const openDropdown = () => {
    if (btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + window.scrollY + 4, left: r.left + window.scrollX, width: Math.max(r.width, 220) });
    }
    setOpen(v => !v);
    setQ('');
  };

  const filtered = options.filter(o => !q || o.toLowerCase().includes(q.toLowerCase())).slice(0, 50);
  const display = value || placeholder || '—';

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4, fontWeight: 600 }}>{label}</div>
      <button ref={btnRef} onClick={openDropdown}
        style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '7px 12px', cursor: 'pointer', color: value ? 'var(--text-primary)' : 'var(--text-muted)', fontSize: 13, minWidth: 180, justifyContent: 'space-between' }}>
        <span className={value ? 'mono' : ''} style={{ fontSize: value ? 12 : 13 }}>{display}</span>
        <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>▾</span>
      </button>
      {open && ReactDOM.createPortal(
        <div ref={popRef} style={{ position: 'absolute', top: pos.top, left: pos.left, width: pos.width, zIndex: 9999, background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow)', overflow: 'hidden' }}>
          <div style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)' }}>
            <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Cari…"
              style={{ width: '100%', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '5px 8px', fontSize: 12, color: 'var(--text-primary)', outline: 'none' }} />
          </div>
          <div style={{ maxHeight: 220, overflowY: 'auto' }}>
            {allowClear && (
              <button onClick={() => { onChange(''); setOpen(false); }}
                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--text-muted)' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                — Semua
              </button>
            )}
            {filtered.map(o => (
              <button key={o} onClick={() => { onChange(o); setOpen(false); }}
                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', background: value === o ? 'rgba(59,130,246,0.12)' : 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: value === o ? 'var(--accent-blue)' : 'var(--text-secondary)', fontFamily: 'var(--font-mono)' }}
                onMouseEnter={e => { if (value !== o) e.currentTarget.style.background = 'var(--bg-hover)'; }}
                onMouseLeave={e => { if (value !== o) e.currentTarget.style.background = 'none'; }}>
                {o}
              </button>
            ))}
            {filtered.length === 0 && <div style={{ padding: '10px 12px', fontSize: 12, color: 'var(--text-muted)' }}>Tidak ada hasil</div>}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// ── Debounced text filter (untuk filter yang gak punya daftar opsi, mis. MAC address) ──
function TextFilterInput({ label, value, onChange, placeholder }) {
  const [local, setLocal] = useState(value || '');

  useEffect(() => { setLocal(value || ''); }, [value]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (local !== (value || '')) onChange(local.trim());
    }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [local]);

  return (
    <div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4, fontWeight: 600 }}>{label}</div>
      <input
        type="text"
        value={local}
        onChange={e => setLocal(e.target.value)}
        placeholder={placeholder}
        style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '7px 10px', color: 'var(--text-primary)', fontSize: 13, outline: 'none', minWidth: 180, fontFamily: 'var(--font-mono)' }}
      />
    </div>
  );
}

// ── Filter bar ────────────────────────────────────────────────────────────────
const FilterBar = ({ filters, onChange, showGranularity, onLoad, loading }) => {
  const { selectedTenant } = useTenant();
  const { user } = useAuth();
  const isOperator = user?.role === 'operator';
  const isUser     = user?.role === 'user';
  const restricted = isOperator || isUser;
  const [userList, setUserList]     = useState([]);
  const [nasList,  setNasList]      = useState([]);
  const [profileList, setProfileList] = useState([]);
  const [realmList, setRealmList]   = useState([]); // hanya dipakai operator (multi-realm)
  const [serverList, setServerList] = useState([]);

  useEffect(() => {
    if (!selectedTenant) return;
    usersAPI.list(selectedTenant, { per_page: 200 }).then(r => setUserList((r.users || []).map(u => u.username))).catch(() => {});
    nasAPI.list(selectedTenant, { per_page: 200 }).then(r => setNasList((r.nas_devices || []).map(n => n.nasname))).catch(() => {});
    profilesAPI.list(selectedTenant, { per_page: 200 }).then(r => setProfileList((r.profiles || []).map(p => p.name))).catch(() => {});
  }, [selectedTenant]);

  useEffect(() => {
    if (!restricted) {
      // superadmin/admin: semua realm untuk dropdown
      tenantsAPI.list({ per_page: 200 }).then(r => setRealmList([...new Set((r.tenants || []).map(t => t.realm).filter(Boolean))])).catch(() => {});
      radiusServersAPI.list({ per_page: 200 }).then(r => setServerList((r.radius_servers || []).map(s => s.name).filter(Boolean))).catch(() => {});
    } else if (isOperator) {
      // operator: hanya realm dari tenant_ids mereka untuk dropdown options
      const tenantIds = user?.tenant_ids || [];
      tenantsAPI.list({ per_page: 200 }).then(r => {
        const tenants = r.tenants || [];
        const myRealms = tenants
          .filter(t => tenantIds.includes(t.id) || tenantIds.includes(String(t.id)))
          .map(t => t.realm)
          .filter(Boolean);
        setRealmList([...new Set(myRealms)]);
      }).catch(() => {});
    }
    // isUser: tidak perlu fetch, realm sudah di-set dari parent dan dropdown hidden
  }, [restricted, isOperator, isUser]);

  const sel = (k, v) => onChange({ ...filters, [k]: v });
  const setWhere = (k, v) => onChange({ ...filters, where: { ...(filters.where || {}), [k]: v } });

  // Group by options: semua role lihat group_by, tapi operator/user tidak lihat server_name
  const visibleGroupByOpts = GROUP_BY_OPTS.filter(g => !restricted || g.value !== 'server_name');

  return (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20, alignItems: 'flex-end' }}>
      {/* Period */}
      <div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4, fontWeight: 600 }}>Period</div>
        <select value={filters.period} onChange={e => sel('period', e.target.value)}
          style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '7px 12px', color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer', outline: 'none' }}>
          {PERIODS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
      </div>

      {filters.period === 'custom' && (<>
        <div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4, fontWeight: 600 }}>Dari</div>
          <input type="datetime-local" value={filters.from || ''} onChange={e => sel('from', e.target.value)}
            style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '7px 10px', color: 'var(--text-secondary)', fontSize: 13, outline: 'none' }} />
        </div>
        <div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4, fontWeight: 600 }}>Sampai</div>
          <input type="datetime-local" value={filters.to || ''} onChange={e => sel('to', e.target.value)}
            style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '7px 10px', color: 'var(--text-secondary)', fontSize: 13, outline: 'none' }} />
        </div>
      </>)}

      {showGranularity && (
        <div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4, fontWeight: 600 }}>Granularitas</div>
          <select value={filters.granularity} onChange={e => sel('granularity', e.target.value)}
            style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '7px 12px', color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer', outline: 'none' }}>
            {GRANULARITIES.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
          </select>
        </div>
      )}

      {/* Group By — semua role tampil, tapi operator/user tidak dapat server_name */}
      <div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4, fontWeight: 600 }}>Group By</div>
        <div style={{ display: 'flex', gap: 6 }}>
          {visibleGroupByOpts.map(g => {
            const cur      = filters.group_by || ['realm'];
            const active   = cur.includes(g.value);
            const autoRealm = g.value === 'realm' && cur.some(v => REALM_FOLLOWERS.includes(v));
            return (
              <button key={g.value}
                onClick={() => sel('group_by', computeGroupBy(cur, g.value))}
                title={autoRealm ? 'Realm otomatis aktif saat username/NAS/profile dipilih' : undefined}
                style={{ fontSize: 12, padding: '6px 12px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', cursor: 'pointer', transition: 'all 0.15s',
                  background: active ? 'rgba(59,130,246,0.15)' : 'var(--bg-input)',
                  color:      active ? 'var(--accent-blue)'    : 'var(--text-secondary)',
                  fontWeight: active ? 600 : 400,
                }}>
                {g.label}{autoRealm ? ' *' : ''}
              </button>
            );
          })}
        </div>
        {(filters.group_by || ['realm']).some(v => REALM_FOLLOWERS.includes(v)) && (
          <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>
            * Realm otomatis ikut aktif
          </div>
        )}
      </div>

      <div style={{ width: 1, alignSelf: 'stretch', background: 'var(--border)', margin: '0 2px' }} />

      {/* Filter Server — hanya superadmin/admin */}
      {!restricted && (
        <SearchSelect label="Server" value={filters.where?.server_name || ''} onChange={v => setWhere('server_name', v)} options={serverList} placeholder="Semua server" />
      )}

      {/* Realm — superadmin/admin: bebas semua; operator: pilih dari tenant_ids tanpa opsi semua; user: hidden */}
      {!isUser && (
        <SearchSelect
          label="Realm"
          value={filters.where?.realm || ''}
          onChange={v => setWhere('realm', v)}
          options={realmList}
          placeholder="Semua realm"
          allowClear={!isOperator}
        />
      )}
      <SearchSelect label="Filter Username" value={filters.where?.username     || ''} onChange={v => setWhere('username', v)}     options={userList}    placeholder="Semua user"    />
      <SearchSelect label="Filter NAS IP"   value={filters.where?.nasipaddress || ''} onChange={v => setWhere('nasipaddress', v)} options={nasList}     placeholder="Semua NAS"     />
      <SearchSelect label="Filter Profile"  value={filters.where?.groupname    || ''} onChange={v => setWhere('groupname', v)}    options={profileList} placeholder="Semua profile" />
      <TextFilterInput label="Filter MAC Address" value={filters.where?.callingstationid || ''} onChange={v => setWhere('callingstationid', v)} placeholder="AA:BB:CC:DD:EE:FF" />

      <Button variant="ghost" onClick={onLoad} loading={loading} style={{ alignSelf: 'flex-end' }}>
        <RefreshCw size={13} /> Refresh
      </Button>
    </div>
  );
};

// ── Histogram tab ─────────────────────────────────────────────────────────────
function HistogramTab({ filters, loading, error, onClearError, onRefresh }) {
  const [data, setData] = useState([]);
  const [keys, setKeys] = useState([]);
  const [meta, setMeta] = useState(null);
  const [seriesLabels, setSeriesLabels] = useState({});
  const [concurrentData, setConcurrentData] = useState([]);
  const [concurrentKeys, setConcurrentKeys] = useState([]);
  const [concurrentSeriesLabels, setConcurrentSeriesLabels] = useState({});
  const [concurrentMeta, setConcurrentMeta] = useState(null);
  const [concurrentLoading, setConcurrentLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = { ...filters, group_by: (filters.group_by || ['username']).join(',') };
      if (params.from) params.from = toCHDatetime(params.from);
      if (params.to)   params.to   = toCHDatetime(params.to);
      if (params.where) {
        Object.entries(params.where).forEach(([k, v]) => { if (!v) delete params.where[k]; });
        if (!Object.keys(params.where).length) delete params.where;
      }
      const res = await analyticsAPI.histogram(params);
      // Backend sekarang balikin { meta, rows } (dulunya array mentah) —
      // tetap dukung shape lama sebagai fallback biar gak crash kalau ada versi backend lama.
      const rows = Array.isArray(res) ? res : (res?.rows || []);
      setMeta(Array.isArray(res) ? null : (res?.meta || null));
      const groupCols = filters.group_by || ['username'];
      const deepCol = deepestGroupCol(groupCols);
      const bucketMap = {};
      const seriesKeys = new Set();
      const labelsMap = {};
      rows.forEach(row => {
        const b = row.bucket;
        if (!bucketMap[b]) bucketMap[b] = { bucket: b };
        // Key internal tetap kombinasi semua level aktif biar tiap kombinasi unik & gak ketiban satu sama lain,
        // tapi LABEL yang ditampilkan cuma level paling dalam yang aktif (leveling: Server > Realm > NAS > Profile > User/MAC).
        const seriesKey = groupCols.map(g => row[g]).filter(Boolean).join('/') || 'total';
        seriesKeys.add(seriesKey);
        if (!labelsMap[seriesKey]) labelsMap[seriesKey] = row[deepCol] ?? seriesKey;
        bucketMap[b][seriesKey + '_up']   = (bucketMap[b][seriesKey + '_up']   || 0) + Number(row.upload_bytes   || 0);
        bucketMap[b][seriesKey + '_down'] = (bucketMap[b][seriesKey + '_down'] || 0) + Number(row.download_bytes || 0);
      });
      setKeys([...seriesKeys]);
      setSeriesLabels(labelsMap);
      setData(Object.values(bucketMap).sort((a, b) => new Date(a.bucket) - new Date(b.bucket)));
    } catch (e) { /* error ditangani parent */ }
  }, [filters]);

  const loadConcurrent = useCallback(async () => {
    setConcurrentLoading(true);
    try {
      const concGroupBy = (filters.group_by || []).filter(g => ['nasipaddress', 'realm', 'server_name'].includes(g));
      const deepCol = deepestGroupCol(concGroupBy);
      const params = { period: filters.period, granularity: filters.granularity };
      if (concGroupBy.length) params.group_by = concGroupBy.join(',');
      if (filters.from) params.from = toCHDatetime(filters.from);
      if (filters.to)   params.to   = toCHDatetime(filters.to);
      if (filters.where) {
        const w = { ...filters.where };
        Object.entries(w).forEach(([k, v]) => { if (!v) delete w[k]; });
        if (Object.keys(w).length) params.where = w;
      }
      const res = await analyticsAPI.concurrent(params);
      const rows = Array.isArray(res) ? res : (res?.rows || []);
      setConcurrentMeta(Array.isArray(res) ? null : (res?.meta || null));
      const bucketMap = {};
      const seriesKeys = new Set();
      const labelsMap = {};
      rows.forEach(row => {
        const b = row.bucket;
        if (!bucketMap[b]) bucketMap[b] = { bucket: b };
        const seriesKey = concGroupBy.map(g => row[g]).filter(Boolean).join('/') || 'all';
        seriesKeys.add(seriesKey);
        if (!labelsMap[seriesKey]) labelsMap[seriesKey] = concGroupBy.length ? (row[deepCol] ?? seriesKey) : 'Semua';
        bucketMap[b][seriesKey + '_users']    = (bucketMap[b][seriesKey + '_users']    || 0) + Number(row.active_users    || 0);
        bucketMap[b][seriesKey + '_sessions'] = (bucketMap[b][seriesKey + '_sessions'] || 0) + Number(row.active_sessions || 0);
      });
      setConcurrentKeys([...seriesKeys]);
      setConcurrentSeriesLabels(labelsMap);
      setConcurrentData(Object.values(bucketMap).sort((a, b) => new Date(a.bucket) - new Date(b.bucket)));
    } catch (e) { /* silent */ }
    finally { setConcurrentLoading(false); }
  }, [filters]);

  useEffect(() => { load(); loadConcurrent(); }, [load, loadConcurrent]);

  return (
    <div>
      {error && <Alert type="error" message={error} onClose={onClearError} />}

      <Card style={{ padding: 20, marginTop: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          Traffic (Upload / Download)
          {meta?.clamped && (
            <span title={meta.note || 'Granularity disesuaikan otomatis'}
              style={{ fontSize: 10, fontWeight: 600, color: 'var(--accent-orange)', background: 'rgba(249,115,22,0.12)', padding: '2px 8px', borderRadius: 999, textTransform: 'none' }}>
              auto: {meta.granularity_used || meta.table}
            </span>
          )}
        </div>
        {loading ? (
          <div style={{ height: 320, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>Loading…</div>
        ) : data.length === 0 ? (
          <div style={{ height: 320, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>Tidak ada data</div>
        ) : (() => {
          const seriesStats = keys.flatMap((k, i) => {
            const color = COLORS[i % COLORS.length];
            const lbl = seriesLabels[k] ?? k;
            return [
              { key: k + '_up',   label: `${lbl} ↑`, color, dash: false },
              { key: k + '_down', label: `${lbl} ↓`, color, dash: true  },
            ].map(s => {
              const vals = data.map(d => Number(d[s.key] || 0)).filter(v => !isNaN(v));
              return { ...s,
                max:  vals.length ? Math.max(...vals) : 0,
                min:  vals.length ? Math.min(...vals) : 0,
                avg:  vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0,
                last: vals.length ? vals[vals.length - 1] : 0,
              };
            });
          });
          return (<>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={data} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="bucket" tickFormatter={fmtBucket} tick={{ fontSize: 11, fill: 'var(--text-muted)' }} />
                <YAxis tickFormatter={fmtBytes} tick={{ fontSize: 11, fill: 'var(--text-muted)' }} width={80} />
                <Tooltip formatter={(val, name) => [fmtBytes(val), name]} labelFormatter={fmtBucket}
                  contentStyle={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', fontSize: 12 }} />
                {keys.map((k, i) => (<React.Fragment key={k}>
                  <Line type="monotone" dataKey={k + '_up'}   name={`${seriesLabels[k] ?? k} ↑`} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey={k + '_down'} name={`${seriesLabels[k] ?? k} ↓`} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={false} strokeDasharray="4 2" />
                </React.Fragment>))}
              </LineChart>
            </ResponsiveContainer>
            <div style={{ marginTop: 16, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead><tr>
                  {['Series','Last','Max','Avg','Min'].map((h, i) => (
                    <th key={h} style={{ padding: '4px 10px', textAlign: i === 0 ? 'left' : 'right', color: 'var(--text-muted)', fontWeight: 600, fontSize: 10, textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {seriesStats.map(s => (
                    <tr key={s.key} onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-card-hover)'} onMouseLeave={e => e.currentTarget.style.background = ''}>
                      <td style={{ padding: '5px 10px', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <svg width="20" height="10"><line x1="0" y1="5" x2="20" y2="5" stroke={s.color} strokeWidth="2" strokeDasharray={s.dash ? '4 2' : 'none'} /></svg>
                        <span style={{ color: s.color, fontWeight: 500 }}>{s.label}</span>
                      </td>
                      <td style={{ padding: '5px 10px', textAlign: 'right', fontFamily: 'var(--font-mono)', color: 'var(--text-primary)', fontWeight: 600 }}>{fmtBytes(s.last)}</td>
                      <td style={{ padding: '5px 10px', textAlign: 'right', fontFamily: 'var(--font-mono)', color: 'var(--accent-red)' }}>{fmtBytes(s.max)}</td>
                      <td style={{ padding: '5px 10px', textAlign: 'right', fontFamily: 'var(--font-mono)', color: 'var(--accent-cyan)' }}>{fmtBytes(s.avg)}</td>
                      <td style={{ padding: '5px 10px', textAlign: 'right', fontFamily: 'var(--font-mono)', color: 'var(--accent-green)' }}>{fmtBytes(s.min)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>);
        })()}
      </Card>

      <Card style={{ padding: 20, marginTop: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          Concurrent Active Users
          {concurrentMeta?.clamped && (
            <span title={concurrentMeta.note || 'Granularity disesuaikan otomatis'}
              style={{ fontSize: 10, fontWeight: 600, color: 'var(--accent-orange)', background: 'rgba(249,115,22,0.12)', padding: '2px 8px', borderRadius: 999, textTransform: 'none' }}>
              auto: {concurrentMeta.granularity_used || concurrentMeta.table}
            </span>
          )}
        </div>
        {concurrentLoading ? (
          <div style={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>Loading…</div>
        ) : concurrentData.length === 0 ? (
          <div style={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>Tidak ada data</div>
        ) : (() => {
          const concStats = concurrentKeys.flatMap((k, i) => {
            const color = COLORS[i % COLORS.length];
            const lbl = concurrentSeriesLabels[k] ?? k;
            return [
              { key: k + '_users',    label: `${lbl} (users)`,    color, dash: false, unit: 'user' },
              { key: k + '_sessions', label: `${lbl} (sessions)`, color, dash: true,  unit: 'sesi' },
            ].map(s => {
              const vals = concurrentData.map(d => Number(d[s.key] || 0)).filter(v => !isNaN(v));
              return { ...s,
                max:  vals.length ? Math.max(...vals) : 0,
                min:  vals.length ? Math.min(...vals) : 0,
                avg:  vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0,
                last: vals.length ? vals[vals.length - 1] : 0,
              };
            });
          });
          return (<>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={concurrentData} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="bucket" tickFormatter={fmtBucket} tick={{ fontSize: 11, fill: 'var(--text-muted)' }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: 'var(--text-muted)' }} width={40} />
                <Tooltip formatter={(val, name) => [val, name]} labelFormatter={fmtBucket}
                  contentStyle={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', fontSize: 12 }} />
                {concurrentKeys.map((k, i) => (<React.Fragment key={k}>
                  <Line type="monotone" dataKey={k + '_users'}    name={`${concurrentSeriesLabels[k] ?? k} (users)`}    stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey={k + '_sessions'} name={`${concurrentSeriesLabels[k] ?? k} (sessions)`} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={false} strokeDasharray="4 2" />
                </React.Fragment>))}
              </LineChart>
            </ResponsiveContainer>
            <div style={{ marginTop: 14, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead><tr>
                  {['Series','Last','Max','Avg','Min'].map((h, i) => (
                    <th key={h} style={{ padding: '4px 10px', textAlign: i === 0 ? 'left' : 'right', color: 'var(--text-muted)', fontWeight: 600, fontSize: 10, textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {concStats.map(s => (
                    <tr key={s.key} onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-card-hover)'} onMouseLeave={e => e.currentTarget.style.background = ''}>
                      <td style={{ padding: '5px 10px', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <svg width="20" height="10"><line x1="0" y1="5" x2="20" y2="5" stroke={s.color} strokeWidth="2" strokeDasharray={s.dash ? '4 2' : 'none'} /></svg>
                        <span style={{ color: s.color, fontWeight: 500 }}>{s.label}</span>
                      </td>
                      <td style={{ padding: '5px 10px', textAlign: 'right', fontFamily: 'var(--font-mono)', color: 'var(--text-primary)', fontWeight: 600 }}>{s.last} {s.unit}</td>
                      <td style={{ padding: '5px 10px', textAlign: 'right', fontFamily: 'var(--font-mono)', color: 'var(--accent-red)' }}>{s.max} {s.unit}</td>
                      <td style={{ padding: '5px 10px', textAlign: 'right', fontFamily: 'var(--font-mono)', color: 'var(--accent-cyan)' }}>{s.avg.toFixed(1)} {s.unit}</td>
                      <td style={{ padding: '5px 10px', textAlign: 'right', fontFamily: 'var(--font-mono)', color: 'var(--accent-green)' }}>{s.min} {s.unit}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>);
        })()}
      </Card>
    </div>
  );
}

// ── Grand Total panel ─────────────────────────────────────────────────────────
function GrandTotalSection({ filters }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = { period: filters.period, group_by: 'total' };
      if (filters.from) params.from = toCHDatetime(filters.from);
      if (filters.to)   params.to   = toCHDatetime(filters.to);
      if (filters.where) {
        const w = { ...filters.where };
        Object.entries(w).forEach(([k, v]) => { if (!v) delete w[k]; });
        if (Object.keys(w).length) params.where = w;
      }
      const raw = await analyticsAPI.usage(params);
      setData(raw);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const METRICS = [
    { label: 'Upload',       key: 'upload_bytes',   fmt: fmtBytes,      color: 'var(--accent-blue)'   },
    { label: 'Download',     key: 'download_bytes',  fmt: fmtBytes,      color: 'var(--accent-cyan)'   },
    { label: 'Total',        key: 'total_bytes',     fmt: fmtBytes,      color: 'var(--accent-green)'  },
    { label: 'Unique Users', key: 'unique_users',    fmt: v => v ?? '—', color: 'var(--accent-orange)' },
    { label: 'Unique NAS',   key: 'unique_nas',      fmt: v => v ?? '—', color: 'var(--accent-purple)' },
    { label: 'Sessions',     key: 'total_sessions',  fmt: v => v ?? '—', color: 'var(--accent-red)'    },
  ];

  return (
    <Card style={{ padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' }}>Grand Total</div>
        <Button variant="ghost" onClick={load} loading={loading} style={{ padding: '5px 10px' }}>
          <RefreshCw size={12} />
        </Button>
      </div>
      {error && <Alert type="error" message={error} onClose={() => setError(null)} />}
      {loading && !data ? (
        <div style={{ height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>Loading…</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
          {METRICS.map(m => (
            <div key={m.label} style={{ padding: '14px 16px', background: 'var(--bg-input)', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', marginBottom: 6 }}>{m.label}</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: m.color, fontFamily: 'var(--font-mono)' }}>
                {data ? m.fmt(data[m.key]) : '—'}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// ── Usage Summary panel ───────────────────────────────────────────────────────
function UsageSummarySection({ filters }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = {
        period:   filters.period,
        group_by: filters.group_by?.length ? filters.group_by.join(',') : 'realm',
      };
      if (filters.from) params.from = toCHDatetime(filters.from);
      if (filters.to)   params.to   = toCHDatetime(filters.to);
      if (filters.where) {
        const w = { ...filters.where };
        Object.entries(w).forEach(([k, v]) => { if (!v) delete w[k]; });
        if (Object.keys(w).length) params.where = w;
      }
      const raw = await analyticsAPI.usage(params);
      setData(Array.isArray(raw) ? raw : []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  // Leveling: sumbu Y graph pakai level PALING DALAM yang aktif (Server > Realm > NAS > Profile > User/MAC),
  // bukan cuma level pertama — biar gak ambigu kalau group by-nya lebih dari satu (mis. realm + username).
  const groupKey = deepestGroupCol(filters.group_by?.length ? filters.group_by : ['realm']);

  return (
    <Card style={{ padding: 20, marginTop: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' }}>Usage Summary</div>
        <Button variant="ghost" onClick={load} loading={loading} style={{ padding: '5px 10px' }}>
          <RefreshCw size={12} />
        </Button>
      </div>
      {error && <Alert type="error" message={error} onClose={() => setError(null)} />}
      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>
      ) : data.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Tidak ada data</div>
      ) : (<>
        <ResponsiveContainer width="100%" height={Math.max(220, data.slice(0, 15).length * 34)}>
          <BarChart data={data.slice(0, 15)} layout="vertical" margin={{ top: 5, right: 30, bottom: 5, left: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
            <XAxis type="number" tickFormatter={fmtBytes} tick={{ fontSize: 11, fill: 'var(--text-muted)' }} />
            <YAxis type="category" dataKey={groupKey} tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} width={110} />
            <Tooltip formatter={(v) => fmtBytes(v)} contentStyle={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', fontSize: 12 }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="upload_bytes"   name="Upload"   fill="var(--accent-blue)" radius={[0,3,3,0]} barSize={14} />
            <Bar dataKey="download_bytes" name="Download" fill="var(--accent-cyan)" radius={[0,3,3,0]} barSize={14} />
          </BarChart>
        </ResponsiveContainer>
        <div style={{ overflowX: 'auto', marginTop: 16, borderTop: '1px solid var(--border)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr>
                {data[0] && Object.keys(data[0]).map(k => (
                  <th key={k} style={{ padding: '8px 10px', textAlign: 'left', color: 'var(--text-muted)', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{k}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((row, i) => (
                <tr key={i} style={{ borderTop: '1px solid var(--border)' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-card-hover)'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}>
                  {Object.entries(row).map(([k, v]) => (
                    <td key={k} style={{ padding: '7px 10px', color: k.includes('bytes') ? 'var(--accent-cyan)' : 'var(--text-secondary)', fontFamily: k.includes('bytes') || k === 'username' || k === 'nasipaddress' ? 'var(--font-mono)' : 'inherit', whiteSpace: 'nowrap' }}>
                      {k.includes('bytes') ? fmtBytes(v) : (v ?? '—')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>)}
    </Card>
  );
}

// ── Top Items section ─────────────────────────────────────────────────────────
const ORDER_BY_OPTS = [
  { value: 'total_bytes',    label: 'Total Bytes'    },
  { value: 'upload_bytes',   label: 'Upload Bytes'   },
  { value: 'download_bytes', label: 'Download Bytes' },
  { value: 'total_sessions', label: 'Sessions'       },
  { value: 'unique_users',   label: 'Unique Users'   },
];
const LIMIT_OPTS = [5, 10, 20, 50, 100];

function TopItemsSection({ filters }) {
  const [orderBy, setOrderBy] = useState('total_bytes');
  const [limit,   setLimit]   = useState(10);
  const [data,    setData]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const groupCols = filters.group_by || ['username'];
      const effectiveOrderBy = (orderBy === 'unique_users' && groupCols.includes('username')) ? 'total_bytes' : orderBy;
      const params = {
        period:   filters.period,
        group_by: groupCols.join(','),
        order_by: effectiveOrderBy,
        limit,
      };
      if (filters.from) params.from = toCHDatetime(filters.from);
      if (filters.to)   params.to   = toCHDatetime(filters.to);
      if (filters.where) {
        const w = { ...filters.where };
        Object.entries(w).forEach(([k, v]) => { if (!v) delete w[k]; });
        if (Object.keys(w).length) params.where = w;
      }
      const raw = await analyticsAPI.top(params);
      setData(Array.isArray(raw) ? raw : (raw?.data || []));
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [filters, orderBy, limit]);

  useEffect(() => { load(); }, [load]);

  const groupCols = filters.group_by || ['username'];
  const orderLabel = ORDER_BY_OPTS.find(o => o.value === orderBy)?.label || orderBy;
  const isBytes = orderBy.includes('bytes');
  // Leveling: nama di chart pakai level paling dalam aja (mis. group by realm+username → tampil username-nya),
  // tabel di bawah tetap nampilin semua kolom group yang aktif per baris.
  const deepCol = deepestGroupCol(groupCols);

  const chartData = data.map(row => ({
    name: row[deepCol] ?? '—',
    value: Number(row[orderBy] || 0),
  }));

  return (
    <Card style={{ padding: 20, marginTop: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' }}>Top Items</div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>Order By</div>
            <div style={{ display: 'flex', gap: 5 }}>
              {ORDER_BY_OPTS.map(o => (
                <button key={o.value} onClick={() => setOrderBy(o.value)}
                  style={{ fontSize: 11, padding: '5px 10px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', cursor: 'pointer', transition: 'all 0.15s',
                    background: orderBy === o.value ? 'rgba(59,130,246,0.15)' : 'var(--bg-input)',
                    color:      orderBy === o.value ? 'var(--accent-blue)'    : 'var(--text-secondary)',
                    fontWeight: orderBy === o.value ? 600 : 400,
                  }}>
                  {o.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>Limit</div>
            <div style={{ display: 'flex', gap: 5 }}>
              {LIMIT_OPTS.map(l => (
                <button key={l} onClick={() => setLimit(l)}
                  style={{ fontSize: 11, padding: '5px 10px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', cursor: 'pointer', transition: 'all 0.15s',
                    background: limit === l ? 'rgba(59,130,246,0.15)' : 'var(--bg-input)',
                    color:      limit === l ? 'var(--accent-blue)'    : 'var(--text-secondary)',
                    fontWeight: limit === l ? 600 : 400,
                  }}>
                  {l}
                </button>
              ))}
            </div>
          </div>
          <Button variant="ghost" onClick={load} loading={loading} style={{ alignSelf: 'flex-end' }}>
            <RefreshCw size={13} /> Refresh
          </Button>
        </div>
      </div>

      {error && <Alert type="error" message={error} onClose={() => setError(null)} />}

      {loading ? (
        <div style={{ height: 260, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>Loading…</div>
      ) : data.length === 0 ? (
        <div style={{ height: 260, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>Tidak ada data</div>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={Math.max(200, chartData.length * 36)}>
            <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 60, bottom: 5, left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
              <XAxis type="number" tickFormatter={isBytes ? fmtBytes : (v => v)} tick={{ fontSize: 11, fill: 'var(--text-muted)' }} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} width={130} />
              <Tooltip
                formatter={(v) => [isBytes ? fmtBytes(v) : v, orderLabel]}
                contentStyle={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', fontSize: 12 }}
              />
              <Bar dataKey="value" name={orderLabel} fill="var(--accent-blue)" radius={[0,3,3,0]} barSize={18}
                label={{ position: 'right', fontSize: 11, fill: 'var(--text-muted)', formatter: (v) => isBytes ? fmtBytes(v) : v }} />
            </BarChart>
          </ResponsiveContainer>

          <div style={{ overflowX: 'auto', marginTop: 16, borderTop: '1px solid var(--border)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr>
                  <th style={{ padding: '6px 10px', textAlign: 'left', color: 'var(--text-muted)', fontSize: 10, fontWeight: 700, textTransform: 'uppercase' }}>#</th>
                  {groupCols.map(c => (
                    <th key={c} style={{ padding: '6px 10px', textAlign: 'left', color: 'var(--text-muted)', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{c}</th>
                  ))}
                  {data[0] && Object.keys(data[0]).filter(k => !groupCols.includes(k)).map(k => (
                    <th key={k} style={{ padding: '6px 10px', textAlign: 'right', color: k === orderBy ? 'var(--accent-blue)' : 'var(--text-muted)', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                      {k === orderBy ? `▼ ${k}` : k}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((row, i) => (
                  <tr key={i} style={{ borderTop: '1px solid var(--border)' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-card-hover)'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}>
                    <td style={{ padding: '7px 10px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 11 }}>{i + 1}</td>
                    {groupCols.map(c => (
                      <td key={c} style={{ padding: '7px 10px', color: 'var(--text-secondary)', fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' }}>{row[c] ?? '—'}</td>
                    ))}
                    {Object.keys(row).filter(k => !groupCols.includes(k)).map(k => {
                      const v = row[k];
                      const isB = k.includes('bytes');
                      const isActive = k === orderBy;
                      return (
                        <td key={k} style={{ padding: '7px 10px', textAlign: 'right', fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap',
                          color: isActive ? 'var(--accent-blue)' : isB ? 'var(--accent-cyan)' : 'var(--text-secondary)',
                          fontWeight: isActive ? 700 : 400 }}>
                          {isB ? fmtBytes(v) : (v ?? '—')}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Card>
  );
}

// ── History tab ───────────────────────────────────────────────────────────────
const HISTORY_LIMIT_OPTS = [25, 50, 100, 250, 500];

function HistoryTab({ filters }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sort, setSort] = useState('desc');
  const [limit, setLimit] = useState(50);
  const [offset, setOffset] = useState(0);

  useEffect(() => { setOffset(0); }, [filters.period, filters.from, filters.to, JSON.stringify(filters.where)]);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = { period: filters.period, sort, limit, offset };
      if (filters.from) params.from = toCHDatetime(filters.from);
      if (filters.to)   params.to   = toCHDatetime(filters.to);
      if (filters.where) {
        const w = { ...filters.where };
        Object.entries(w).forEach(([k, v]) => { if (!v) delete w[k]; });
        if (Object.keys(w).length) params.where = w;
      }
      const raw = await analyticsAPI.history(params);
      setData(raw?._array || raw?.rows || (Array.isArray(raw) ? raw : []));
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [filters, sort, limit, offset]);

  useEffect(() => { load(); }, [load]);

  const COLUMNS = [
    { key: 'radacctid',          label: 'ID',             fmt: v => v ?? '—',                        mono: true  },
    { key: 'acctstarttime',      label: 'Mulai',          fmt: fmtBucketFull,                         mono: false },
    { key: 'acctstoptime',       label: 'Selesai',        fmt: v => v ? fmtBucketFull(v) : '—',      mono: false },
    { key: 'duration_seconds',   label: 'Durasi',         fmt: fmtDuration,                          mono: true  },
    { key: 'username',           label: 'Username',       fmt: v => v ?? '—',                        mono: true  },
    { key: 'realm',              label: 'Realm',          fmt: v => v ?? '—',                        mono: true  },
    { key: 'nasipaddress',       label: 'NAS IP',         fmt: v => v ?? '—',                        mono: true  },
    { key: 'server_name',        label: 'Server',         fmt: v => v ?? '—',                        mono: true  },
    { key: 'groupname',          label: 'Profile',        fmt: v => v ?? '—',                        mono: true  },
    { key: 'framedipaddress',    label: 'Framed IP',      fmt: v => v ?? '—',                        mono: true  },
    { key: 'acctinputoctets',    label: 'Upload',         fmt: fmtBytes, mono: true, align: 'right', color: 'var(--accent-blue)'  },
    { key: 'acctoutputoctets',   label: 'Download',       fmt: fmtBytes, mono: true, align: 'right', color: 'var(--accent-cyan)'  },
    { key: 'total_bytes',        label: 'Total',          fmt: fmtBytes, mono: true, align: 'right', color: 'var(--accent-green)' },
    { key: 'acctterminatecause', label: 'Terminate Cause',fmt: v => v || '—',                        mono: true  },
  ];

  const isFirstPage = offset === 0;
  const isLastPage  = data.length < limit;

  return (
    <Card style={{ padding: 20, marginTop: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' }}>History</div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>Urutan</div>
            <div style={{ display: 'flex', gap: 5 }}>
              {[{ v: 'desc', l: 'Terbaru' }, { v: 'asc', l: 'Terlama' }].map(o => (
                <button key={o.v} onClick={() => { setSort(o.v); setOffset(0); }}
                  style={{ fontSize: 11, padding: '5px 10px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', cursor: 'pointer', transition: 'all 0.15s',
                    background: sort === o.v ? 'rgba(59,130,246,0.15)' : 'var(--bg-input)',
                    color:      sort === o.v ? 'var(--accent-blue)'    : 'var(--text-secondary)',
                    fontWeight: sort === o.v ? 600 : 400,
                  }}>
                  {o.l}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>Per Halaman</div>
            <div style={{ display: 'flex', gap: 5 }}>
              {HISTORY_LIMIT_OPTS.map(l => (
                <button key={l} onClick={() => { setLimit(l); setOffset(0); }}
                  style={{ fontSize: 11, padding: '5px 10px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', cursor: 'pointer', transition: 'all 0.15s',
                    background: limit === l ? 'rgba(59,130,246,0.15)' : 'var(--bg-input)',
                    color:      limit === l ? 'var(--accent-blue)'    : 'var(--text-secondary)',
                    fontWeight: limit === l ? 600 : 400,
                  }}>
                  {l}
                </button>
              ))}
            </div>
          </div>
          <Button variant="ghost" onClick={load} loading={loading} style={{ alignSelf: 'flex-end' }}>
            <RefreshCw size={13} /> Refresh
          </Button>
        </div>
      </div>

      {error && <Alert type="error" message={error} onClose={() => setError(null)} />}

      {loading ? (
        <div style={{ height: 260, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>Loading…</div>
      ) : data.length === 0 ? (
        <div style={{ height: 260, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>Tidak ada data</div>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr>
                  {COLUMNS.map(c => (
                    <th key={c.key} style={{ padding: '6px 10px', textAlign: c.align || 'left', color: 'var(--text-muted)', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((row, i) => (
                  <tr key={row.radacctid || i} style={{ borderTop: '1px solid var(--border)' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-card-hover)'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}>
                    {COLUMNS.map(c => (
                      <td key={c.key}
                        style={{ padding: '7px 10px', textAlign: c.align || 'left', whiteSpace: 'nowrap',
                          fontFamily: c.mono ? 'var(--font-mono)' : 'inherit',
                          color: c.color || 'var(--text-secondary)' }}>
                        {c.fmt(row[c.key])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              Menampilkan {offset + 1}–{offset + data.length}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button variant="ghost" disabled={isFirstPage} onClick={() => setOffset(Math.max(0, offset - limit))} style={{ padding: '5px 10px' }}>
                <ChevronLeft size={14} /> Sebelumnya
              </Button>
              <Button variant="ghost" disabled={isLastPage} onClick={() => setOffset(offset + limit)} style={{ padding: '5px 10px' }}>
                Selanjutnya <ChevronRight size={14} />
              </Button>
            </div>
          </div>
        </>
      )}
    </Card>
  );
}

// ── Main Analytics page ───────────────────────────────────────────────────────
export default function Analytics() {
  const { user } = useAuth();
  const isOperator = user?.role === 'operator';
  const isUser     = user?.role === 'user';

  const [activeTab, setActiveTab] = useState('summary');
  const [filters, setFilters] = useState({ period: '1h', granularity: '5m', group_by: ['realm'] });
  const [realmReady, setRealmReady] = useState(false);
  const lastNonCustomPeriod = useRef('1h');

  // Init realm untuk operator/user di parent — hindari race condition dengan tab content
  useEffect(() => {
    if (!isOperator && !isUser) {
      setRealmReady(true);
      return;
    }
    const tenantIds = user?.tenant_ids || [];
    tenantsAPI.list({ per_page: 200 }).then(r => {
      const tenants = r.tenants || [];
      const myTenants = tenants.filter(t =>
        tenantIds.includes(t.id) || tenantIds.includes(String(t.id))
      );
      const realm = myTenants[0]?.realm;
      if (realm) {
        setFilters(f => ({ ...f, where: { ...(f.where || {}), realm } }));
      }
      setRealmReady(true);
    }).catch(() => setRealmReady(true));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFiltersChange = useCallback((next) => {
    if (next.period === 'custom' && filters.period !== 'custom') {
      const now = new Date();
      const prev = lastNonCustomPeriod.current;
      let fromDate = new Date(now);
      if      (prev === '1h')    fromDate = new Date(now - 1  * 3600000);
      else if (prev === '24h')   fromDate = new Date(now - 24 * 3600000);
      else if (prev === '7d')    fromDate = new Date(now - 7  * 86400000);
      else if (prev === '30d')   fromDate = new Date(now - 30 * 86400000);
      else if (prev === 'today') { fromDate = new Date(now); fromDate.setHours(0, 0, 0, 0); }
      else if (prev === 'month') { fromDate = new Date(now); fromDate.setDate(1); fromDate.setHours(0, 0, 0, 0); }
      const toLocal = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
      setFilters({ ...next, from: toLocal(fromDate), to: toLocal(now) });
    } else {
      if (next.period && next.period !== 'custom') lastNonCustomPeriod.current = next.period;
      setFilters(next);
    }
  }, [filters.period]);

  const TABS = [
    { key: 'summary',   label: 'Summary'   },
    { key: 'top',       label: 'Top Items' },
    { key: 'histogram', label: 'Histogram' },
    { key: 'history',   label: 'History'   },
  ];

  const showGranularity = activeTab === 'histogram';

  return (
    <div className="animate-in">
      <PageHeader title="Analytics" subtitle="Traffic & usage data dari ClickHouse" />

      <FilterBar
        filters={filters}
        onChange={handleFiltersChange}
        showGranularity={showGranularity}
        onLoad={() => {}}
        loading={false}
      />

      <div style={{ display: 'flex', gap: 2, marginBottom: 20, borderBottom: '1px solid var(--border)', marginTop: 4 }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            style={{
              padding: '9px 20px', fontSize: 13, fontWeight: activeTab === t.key ? 600 : 400,
              background: 'none', border: 'none', cursor: 'pointer',
              borderBottom: activeTab === t.key ? '2px solid var(--accent-blue)' : '2px solid transparent',
              color: activeTab === t.key ? 'var(--accent-blue)' : 'var(--text-secondary)',
              marginBottom: -1, transition: 'all 0.15s',
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {!realmReady ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Loading…</div>
      ) : (<>
        {activeTab === 'summary' && (
          <div>
            <GrandTotalSection filters={filters} />
            <UsageSummarySection filters={filters} />
          </div>
        )}
        {activeTab === 'top'       && <TopItemsSection filters={filters} />}
        {activeTab === 'histogram' && <HistogramTab    filters={filters} />}
        {activeTab === 'history'   && <HistoryTab      filters={filters} />}
      </>)}
    </div>
  );
}